import { escaparHtml, formatearMoneda } from '@plataforma/utils';

import { agregarProducto } from './carrito.js';
import { iconoLucide } from './iconos.js';
import { clasesBotones, clasesTarjetas } from './motortema.js';

export { escaparHtml };

/**
 * @param {object} tienda
 * @param {string} [texto]
 */
export function botonWhatsAppFlotanteHtml(tienda, texto = 'Hola, quiero mas informacion') {
  const planNombre = String(tienda?.empresa?.plan_nombre ?? 'Prueba');
  if (planNombre !== 'Medio' && planNombre !== 'Pro') return '';

  const href = enlaceWhatsApp(tienda, texto);
  if (href === '/contacto') return '';

  return `
    <a class="sp-whatsapp-flotante" href="${href}" target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="white" width="28">
        <path d="M16 .4C7.4.4.4 7.3.4 16c0 2.8.7 5.5 2.1 7.9L.1 32l8.3-2.2c2.3 1.2 4.9 1.8 7.6 1.8 8.7 0 15.6-7 15.6-15.6C31.6 7.3 24.7.4 16 .4zm8.6 22.6c-.4 1.1-2.1 2-2.9 2.1-.7.1-1.5.1-2.4-.1-.6-.2-1.3-.4-2.3-.8-4.1-1.8-6.8-5.9-7-6.2-.2-.3-1.7-2.3-1.7-4.4 0-2.1 1.1-3.1 1.5-3.5.4-.4.9-.5 1.2-.5h.9c.3 0 .7-.1 1.1.8.4.9 1.4 3.4 1.5 3.6.1.3.2.6 0 .9-.2.3-.3.5-.5.7-.2.2-.5.5-.7.7-.2.2-.4.4-.2.8.2.4 1 1.7 2.1 2.7 1.5 1.4 2.7 1.8 3.1 2 .4.2.6.2.8-.1.2-.3 1-1.2 1.2-1.6.2-.4.5-.3.8-.2.3.1 2 .9 2.4 1.1.4.2.6.3.7.5.1.2.1 1-.3 2.1z"/>
      </svg>
      <span class="sp-whatsapp-flotante__texto">¿Te ayudo?</span>
    </a>
  `;
}

/** @param {object} producto */
export function imagenPrincipal(producto) {
  return (
    producto.imagenes?.find((imagen) => imagen.es_principal) ??
    producto.imagenes?.[0] ?? { url: '', texto_alternativo: producto.nombre }
  );
}

/** @param {object} producto */
export function etiquetasProducto(producto) {
  return [
    producto.es_nuevo ? 'Nuevo' : null,
    producto.es_oferta ? 'Oferta' : null,
    producto.es_destacado ? 'Destacado' : null,
  ].filter(Boolean);
}

/**
 * Tarjeta de producto de la grilla. `estado` (opcional) es el estado visual del
 * motor de temas (Parte 2): aplica estilo/radio/sombra de tarjeta y estilo de
 * boton del editor. Sin él, usa los valores por defecto (redondeado/borde).
 *
 * @param {object} producto
 * @param {string} moneda
 * @param {object} [estado]
 */
export function tarjetaProductoHtml(producto, moneda = 'PEN', estado) {
  const imagen = imagenPrincipal(producto);
  const etiquetas = etiquetasProducto(producto)
    .map((etiqueta) => `<span class="sp-chip">${escaparHtml(etiqueta)}</span>`)
    .join('');
  const tarjeta = clasesTarjetas(estado);
  const boton = clasesBotones(estado, 'sp-boton--primario sp-boton--agregar');

  return `
    <article class="sp-producto ${tarjeta}">
      <a class="sp-producto__media" href="/producto/${escaparHtml(producto.slug)}">
        <img src="${escaparHtml(imagen.url)}" alt="${escaparHtml(imagen.texto_alternativo ?? producto.nombre)}" loading="lazy" decoding="async" />
        ${
          producto.precio_anterior
            ? `<span class="sp-chip sp-chip--oferta">${iconoLucide('Tag')}Oferta</span>`
            : ''
        }
      </a>
      <div class="sp-producto__contenido">
        <div class="sp-producto__chips">${etiquetas}</div>
        <h3><a href="/producto/${escaparHtml(producto.slug)}">${escaparHtml(producto.nombre)}</a></h3>
        <p>${escaparHtml(producto.descripcion_corta ?? '')}</p>
        <div class="sp-producto__precio">
          <strong>${formatearMoneda(Number(producto.precio), moneda)}</strong>
          ${
            producto.precio_anterior
              ? `<span>${formatearMoneda(Number(producto.precio_anterior), moneda)}</span>`
              : ''
          }
        </div>
        <button class="${boton}" type="button" data-agregar-producto="${escaparHtml(producto.slug)}">
          ${iconoLucide('ShoppingCart')}<span>Agregar</span>
        </button>
      </div>
    </article>
  `;
}

/** @param {ParentNode} contenedor */
export function conectarBotonesAgregar(contenedor) {
  contenedor.querySelectorAll('[data-agregar-producto]').forEach((boton) => {
    boton.addEventListener('click', () => {
      agregarProducto(boton.dataset.agregarProducto, 1);
      boton.classList.add('sp-boton--ok');
      boton.innerHTML = `${iconoLucide('ShoppingBag')}<span>Agregado</span>`;
      window.setTimeout(() => {
        boton.classList.remove('sp-boton--ok');
        boton.innerHTML = `${iconoLucide('ShoppingCart')}<span>Agregar</span>`;
      }, 900);
    });
  });
}

/**
 * @param {object} tienda
 * @param {string} [texto]
 */
export function enlaceWhatsApp(tienda, texto = 'Hola, quiero más información') {
  const telefono = String(tienda.perfil.whatsapp ?? '').replace(/\D/g, '');
  if (!telefono) return '/contacto';
  return `https://wa.me/${telefono}?text=${encodeURIComponent(texto)}`;
}
